import { useState, useMemo, useEffect } from "react";
// === ADDED BY SAMPATH – 2026-01-29 === (useEffect added)
// === END SAMPATH ===

import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { useAppSelector, useAppDispatch } from "hooks/store";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { setdeliveryType } from "store/slices/main.slice";

// === ADDED BY SAMPATH – 2026-01-29 ===
// Icons   style
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
// === END SAMPATH ===

interface OrderTypePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (orderType: "pickup" | "delivery") => void;
}

const OrderTypePopup: React.FC<OrderTypePopupProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const { settings, companyData, deliveryType } = useAppSelector(({ main }) => main);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Use Redux deliveryType as the source of truth (same as Cart.tsx)
  const initialType = (deliveryType === "pickup" ? "pickup" : "delivery") as
    | "pickup"
    | "delivery";
  // === END SAMPATH ===

  const [orderType, setOrderType] = useState<"pickup" | "delivery">(initialType);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Respect store closed flags (same logic style as Cart.tsx)
  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  // When BOTH are disabled, show one clear red message too
  const isAllDisabled = isPickupDisabled && isDeliveryDisabled;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Prevent closing the modal when clicking the backdrop (background area)
  const handleModalClose = (_event: any, reason?: string) => {
    if (reason === "backdropClick") return; // disable background click close
    onClose(); // keep other close behaviors (e.g., ESC) working
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Build a clean, centered location line (same fields style as ToolBar.tsx)
  const locationText = useMemo(() => {
    const a1 = (companyData?.address1 ?? "").trim();
    const a2 = (companyData?.address2 ?? "").trim();
    const full = `${a1}${a1 && a2 ? " " : ""}${a2}`.trim();
    return full;
  }, [companyData?.address1, companyData?.address2]);

  const showLocationUnderName =
    !!settings?.DisplayLocationOnCheckout && !!locationText;

  // Better red messages per order type
  const pickupUnavailableLabel = "Pickup not available today";
  const deliveryUnavailableLabel = "Delivery not available today";
  const allUnavailableLabel = "We’re not accepting orders today";
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Click-to-start flow (no Continue button)
  const handleStartOrder = (type: "pickup" | "delivery") => {
    if (type === "pickup" && isPickupDisabled) return;
    if (type === "delivery" && isDeliveryDisabled) return;

    setOrderType(type);
    dispatch(setdeliveryType(type));

    // keep existing prop available for other integrations
    try {
      onSubmit?.(type);
    } catch {
      // no-op
    }

    onClose();
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // When popup opens, sync selection BUT DO NOT auto-close when only one option is available
  useEffect(() => {
    if (!open) return;

    // If only one is available, just preselect it (keep popup open)
    if (isPickupDisabled && !isDeliveryDisabled) {
      setOrderType("delivery");
      dispatch(setdeliveryType("delivery"));
      return;
    }
    if (isDeliveryDisabled && !isPickupDisabled) {
      setOrderType("pickup");
      dispatch(setdeliveryType("pickup"));
      return;
    }

    // Otherwise keep Redux active selection
    setOrderType(initialType);
  }, [open, initialType, isPickupDisabled, isDeliveryDisabled, dispatch]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Button outline color   (gold/orange feel using primary mix)
  //const outlineBorder = "rgba(0,0,0,0.25)";
  const outlineBorder = "rgb(var(--color-primary) / 0.55)";
  // === END SAMPATH ===

  return (
    <Modal
      open={open}
      onClose={handleModalClose} // === ADDED BY SAMPATH – 2026-01-05 === // === END SAMPATH ===
      className="flex items-center justify-center"
      slotProps={{
        backdrop: {
          onClick: (e) => e.stopPropagation(),
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.69)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      }}
    >
      {/* === ADDED BY SAMPATH – 2026-01-29 === */}
      {/* Clean white section layout (NOT card style)  */}
      <div className="w-[96vw] max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header area */}
        <div className="px-6 pt-6 pb-4 flex flex-col items-center">
          {settings?.showLogo !== 0 ? (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative"
            >
              <img
                src={companyData?.logoUrl}
                alt=""
                className="mb-3 max-h-20 max-w-[260px] object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
          ) : (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative bg-black/5 p-2 rounded-xl"
            >
              <AdminPanelSettingsIcon className="text-primary" />
            </div>
          )}

          {!!settings.ShowCompanyName && !!companyData?.companyName && (
            <div className="w-full flex flex-col items-center">
              <h1 className="text-xl font-bold text-primary text-center leading-tight break-words whitespace-normal w-full">
                {companyData?.companyName}
              </h1>

              {showLocationUnderName && (
                <p
                  className="mt-1 text-xs text-center text-primary/80 tracking-wide uppercase leading-snug break-words whitespace-normal w-full"
                  title={locationText}
                >
                  {locationText}
                </p>
              )}

              {/* If both services are closed, show one clear red status message */}
              {isAllDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600 text-center">
                  {allUnavailableLabel}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Body area - 2 columns with a vertical divider */}
        <div className="px-6 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch">
            {/* Pickup */}
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <LocalMallOutlinedIcon sx={{ fontSize: 52 }} className="text-black/70" />

              <div className="mt-5 text-sm font-extrabold tracking-[0.25em] text-black/80">
                ORDER PICK UP
              </div>

              {isPickupDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {pickupUnavailableLabel}
                </div>
              )}

              <Button
                variant="outlined"
                disabled={isPickupDisabled}
                onClick={() => handleStartOrder("pickup")}
                sx={{
                  mt: 3,
                  px: 5,
                  py: 1.2,
                  borderRadius: "0px",
                  borderColor: outlineBorder,
                  color: "rgba(0,0,0,0.75)",
                  letterSpacing: "0.18em",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "rgb(var(--color-primary) / 0.95)",
                    borderWidth: "2px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  },
                }}
              >
                START YOUR ORDER
              </Button>
            </div>

            {/* Divider */}
            <div className="hidden md:flex justify-center">
              <div className="w-px bg-black/15 my-8" />
            </div>

            {/* Delivery */}
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <LocalShippingOutlinedIcon sx={{ fontSize: 52 }} className="text-black/70" />

              <div className="mt-5 text-sm font-extrabold tracking-[0.25em] text-black/80">
                GET DELIVERY
              </div>

              {isDeliveryDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {deliveryUnavailableLabel}
                </div>
              )}

              <Button
                variant="outlined"
                disabled={isDeliveryDisabled}
                onClick={() => handleStartOrder("delivery")}
                sx={{
                  mt: 3,
                  px: 5,
                  py: 1.2,
                  borderRadius: "0px",
                  borderColor: outlineBorder,
                  color: "rgba(0,0,0,0.75)",
                  letterSpacing: "0.18em",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "rgb(var(--color-primary) / 0.95)",
                    borderWidth: "2px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  },
                }}
              >
                START YOUR ORDER
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* === END SAMPATH === */}
    </Modal>
  );
};

export default OrderTypePopup;
